import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';

import { MatSnackBar } from '@angular/material/snack-bar';

import { skip, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { OsintSearchService } from '../../../services/osint-search/osint-search.service';
import { OsintWalletExportService } from '../../../services/osint-wallet-export/osint-wallet-export.service';
import { isOsintJobVendorSyncComplete } from '../../../services/search-polling/osint-job-poll-response.util';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { SearchServiceStatusService } from '../../../services/search-service-status/search-service-status.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { OsintAiAnalysisComponent } from '../osint-ai-analysis/osint-ai-analysis.component';

@Component({
  selector: 'app-osint-wallet-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    HeaderComponent,
    SidebarComponent,
    OsintAiAnalysisComponent,
  ],
  templateUrl: './osint-wallet-search-result.component.html',
  styleUrl: './osint-wallet-search-result.component.css',
})
export class OsintWalletSearchResultComponent implements OnInit, OnDestroy {
  readonly Math = Math;

  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;
  jobResultSubscription?: Subscription;
  private vendorSyncSubscription?: Subscription;

  notes: string = '';
  activeViewTab: 'results' | 'ai-analysis' = 'results';
  activeNavSection: string | null = null;

  get isOsintAiAnalysisEnabled(): boolean {
    return this.searchServiceStatusService.isOsintAiAnalysisEnabled();
  }
  isAtTop: boolean = true;
  showAllTx: boolean = false;
  showFlowFilters = false;
  flowMinAmt = '';
  /** Stagger offsets for animated flow arrows along each Sankey link. */
  readonly flowArrowDelays = ['0s', '0.75s', '1.5s'];
  readonly flowArrowDuration = '2.6s';
  showTxFilters = false;
  txDirFilter: 'all' | 'in' | 'out' = 'all';
  txMinAmt = '';
  txMaxAmt = '';
  txFromDate = '';
  txToDate = '';
  traceDirection: 'downstream' | 'upstream' = 'downstream';
  traceDepth: number = 4;
  traceLoading: boolean = false;
  traceError: string | null = null;
  traceResult: any = null;
  traceHistory: any[] = [];
  traceHistoryLoading = false;
  previewDraft: boolean = false;
  exportingFormat: 'pdf' | 'zip' | null = null;

  /** Investigation payload from wallet investigate API (3rd-party shape). */
  inv: any = null;

  /** BTC deep-scan overlay (vendor GET /api/deep-scan). */
  deepScan: any = null;
  deepScanConsent: 'pending' | 'approved' | 'declined' = 'pending';
  deepScanConfirming = false;
  deepScanPreviewLoading = false;
  deepScanCreditError: string | null = null;
  private deepScanTimer: ReturnType<typeof setTimeout> | null = null;
  private deepScanPollGeneration = 0;

  /** Community report form. */
  readonly reportCategories = [
    'Scam',
    'Phishing',
    'Ransomware',
    'Stolen Funds',
    'Fake Exchange',
    'Extortion',
    'Sextortion',
    'Impersonation',
    'Fraudulent Investment',
    'Other',
  ];
  reportModalOpen = false;
  reportCategory = '';
  reportNote = '';
  reportSubmitting = false;

  /** Inline transaction lookup from timeline. */
  txLookupLoading = false;
  txLookupResult: any = null;
  txLookupError: string | null = null;

  /** KPI “View details” slide-over (matches Ryno DetailDrawer). */
  detailDrawer: 'received' | 'sent' | 'balance' | 'transactions' | 'activity' | 'abuse' | null =
    null;

  /** Open bilingual info popover id (Ryno InfoHint). */
  activeInfoHint: string | null = null;
  infoHintPlacement: 'top' | 'bottom' = 'top';
  infoHintStyle: Record<string, string> | null = null;
  private infoHintToggleAt = 0;

  /** Fixed-position volume tooltip (escapes card overflow clipping). */
  volumeTooltip: {
    label: string;
    received: number;
    sent: number;
    left: number;
    top: number;
  } | null = null;

  private readonly drawerMeta: Record<
    string,
    { title: string; icon: string; iconClass: string }
  > = {
    received: {
      title: 'Total Received',
      icon: 'fa-coins',
      iconClass: 'text-amber',
    },
    sent: {
      title: 'Total Sent',
      icon: 'fa-arrow-up-right-from-square',
      iconClass: 'text-red',
    },
    balance: {
      title: 'Balance',
      icon: 'fa-wallet',
      iconClass: 'text-emerald',
    },
    transactions: {
      title: 'Transactions',
      icon: 'fa-hashtag',
      iconClass: 'text-blue',
    },
    activity: {
      title: 'Activity & Age',
      icon: 'fa-calendar',
      iconClass: 'text-purple',
    },
    abuse: {
      title: 'Abuse & Threat Screening',
      icon: 'fa-triangle-exclamation',
      iconClass: 'text-red',
    },
  };

  private sectionObserver?: IntersectionObserver;
  private scrollHostListener?: () => void;

  get isLoadingResults(): boolean {
    return (
      this.loadingCompletedSearchResult ||
      this.loadingPolledSearchResult ||
      (!this.inv &&
        Boolean(this.selectedSearchResult) &&
        !this.selectedSearchResult?.done)
    );
  }

  get displayAddress(): string {
    return (
      this.selectedSearchResult?.searched_wallet ||
      this.inv?.address ||
      ''
    );
  }

  get exportBusy(): boolean {
    return this.exportingFormat !== null;
  }

  get totalAbuse(): number {
    return (
      (this.inv?.abuseCount || 0) +
      (this.inv?.communityReportCount || 0) +
      (this.inv?.threatFlags?.length || 0)
    );
  }

  /** Ryno: Math.round(attributedValueShare * 100) — share is a 0–1 fraction. */
  get leaAttributedValuePct(): number {
    const share = Number(this.inv?.lea?.coverage?.attributedValueShare ?? 0);
    if (!Number.isFinite(share)) return 0;
    return Math.round(share * 100);
  }

  get leaActionableCount(): number {
    return (this.inv?.lea?.targets || []).filter((t: any) => t?.actionable).length;
  }

  get exposureHitCount(): number {
    return this.inv?.exposure?.hits?.length ?? 0;
  }

  /** Risk highlight class for Analyst Narrative (matches Ryno riskColor). */
  get narrativeRiskClass(): string {
    const level = String(this.inv?.riskLevel || '').toLowerCase();
    if (
      level.includes('high') ||
      level.includes('severe') ||
      level.includes('critical')
    ) {
      return 'hl-red';
    }
    if (
      level.includes('medium') ||
      level.includes('moderate') ||
      level.includes('caution')
    ) {
      return 'hl-amber';
    }
    return 'hl-green';
  }

  /** Ryno narrative native amount (max 4 fraction digits). */
  fmtNarrativeBtc(n: number | null | undefined): string {
    if (typeof n !== 'number' || Number.isNaN(n)) return '0';
    return n.toLocaleString('en-US', { maximumFractionDigits: 4 });
  }

  /** Ryno narrative fiat: "$x / ₹y" (omit missing legs). */
  narrativeFiatAbout(
    u: number | null | undefined,
    i: number | null | undefined
  ): string | null {
    const us =
      typeof u === 'number' && Number.isFinite(u)
        ? `$${u.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
        : null;
    const is =
      typeof i === 'number' && Number.isFinite(i)
        ? `₹${i.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
        : null;
    if (us && is) return `${us} / ${is}`;
    return us ?? is ?? null;
  }

  get riskTone(): string {
    const score = this.inv?.riskScore ?? 0;
    if (score < 30) return 'low';
    if (score <= 60) return 'medium';
    return 'high';
  }

  get riskIcon(): string {
    const score = this.inv?.riskScore ?? 0;
    if (score < 30) return 'fa-shield-halved';
    if (score <= 60) return 'fa-triangle-exclamation';
    return 'fa-shield';
  }

  get needsDeepScan(): boolean {
    return (
      this.inv?.chain === 'btc' &&
      this.inv?.counterpartyScan?.complete === false
    );
  }

  get deepScanActive(): boolean {
    return (
      !!this.deepScan &&
      (this.deepScan.status === 'running' || this.deepScan.status === 'done')
    );
  }

  get deepScanDone(): boolean {
    return this.deepScan?.status === 'done';
  }

  /** Prefer deep-scan counterparties when available (matches Ryno UI). */
  get displayTopSenders(): any[] {
    if (
      this.deepScanActive &&
      (this.deepScan?.topSenders?.length ?? 0) > 0
    ) {
      return this.deepScan.topSenders;
    }
    return this.inv?.topSenders || [];
  }

  get displayTopReceivers(): any[] {
    if (
      this.deepScanActive &&
      (this.deepScan?.topReceivers?.length ?? 0) > 0
    ) {
      return this.deepScan.topReceivers;
    }
    return this.inv?.topReceivers || [];
  }

  /** Full-history aggregates once deep scan finishes (Ryno `fullHistory`). */
  get fullHistory(): {
    analyzed: number;
    inCount: number;
    outCount: number;
    inVolume: number;
    outVolume: number;
    firstSeen: string | null;
    lastSeen: string | null;
  } | null {
    if (
      !this.deepScanDone ||
      (this.deepScan?.inCount == null && this.deepScan?.outCount == null)
    ) {
      return null;
    }
    return {
      analyzed: this.deepScan?.analyzed ?? 0,
      inCount: this.deepScan?.inCount ?? 0,
      outCount: this.deepScan?.outCount ?? 0,
      inVolume: this.deepScan?.inVolume ?? 0,
      outVolume: this.deepScan?.outVolume ?? 0,
      firstSeen: this.deepScan?.firstSeenTs
        ? new Date(this.deepScan.firstSeenTs * 1000).toISOString()
        : null,
      lastSeen: this.deepScan?.lastSeenTs
        ? new Date(this.deepScan.lastSeenTs * 1000).toISOString()
        : null,
    };
  }

  get effectiveFirstSeen(): string | null {
    const profile = this.inv?.profile?.firstSeen ?? null;
    const full = this.fullHistory?.firstSeen ?? null;
    const a = profile ? new Date(profile).getTime() : 0;
    const b = full ? new Date(full).getTime() : 0;
    if (b > 0 && (a === 0 || b < a)) return full;
    return profile;
  }

  get effectiveLastSeen(): string | null {
    const profile = this.inv?.profile?.lastSeen ?? null;
    const full = this.fullHistory?.lastSeen ?? null;
    const a = profile ? new Date(profile).getTime() : 0;
    const b = full ? new Date(full).getTime() : 0;
    if (b > a) return full;
    return profile;
  }

  get effectiveAddressAge(): string {
    const first = this.effectiveFirstSeen;
    const profileFirst = this.inv?.profile?.firstSeen ?? null;
    const t = first ? new Date(first).getTime() : NaN;
    const p = profileFirst ? new Date(profileFirst).getTime() : NaN;
    if (Number.isNaN(t) || (!Number.isNaN(p) && t === p)) {
      return this.inv?.profile?.addressAge || 'Unknown';
    }
    const days = Math.max(0, Math.floor((Date.now() - t) / 86400000));
    return days > 365
      ? `${Math.floor(days / 365)} year(s), ${days % 365} day(s)`
      : `${days} day(s)`;
  }

  get daysSinceLastActivity(): number | null {
    if (!this.effectiveLastSeen) return null;
    const t = new Date(this.effectiveLastSeen).getTime();
    if (Number.isNaN(t)) return null;
    return Math.max(0, Math.floor((Date.now() - t) / 86400000));
  }

  get displayNote(): string {
    if (this.deepScanDone) {
      const n = (this.deepScan?.analyzed ?? 0).toLocaleString('en-US');
      if (this.fullHistory) {
        return `Full history analyzed: top senders/receivers, first-seen date and the incoming/outgoing counts cover all ${n} transactions of this address.`;
      }
      return `Full history analyzed: top senders/receivers cover all ${n} transactions of this address.`;
    }
    return this.inv?.note || '';
  }

  /** Date label for a reused stored deep scan (Ryno `scannedAt`). */
  get storedScanDateLabel(): string | null {
    const scannedAt = this.deepScan?.scannedAt;
    if (scannedAt == null || scannedAt === '') return null;
    try {
      return new Date(scannedAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
    } catch {
      return null;
    }
  }

  /**
   * Deep-scan completion banner copy (reused / top-up / full), matching Ryno.
   */
  get deepScanDoneMessage(): string {
    if (!this.deepScanDone) return '';

    const analyzed = (this.deepScan?.analyzed ?? 0).toLocaleString('en-US');
    const failedPages = Number(this.deepScan?.failedPages ?? 0);
    const failedSuffix =
      failedPages > 0
        ? ` (${failedPages} history pages could not be retrieved)`
        : '';
    const coverSuffix = this.fullHistory
      ? ' Top senders/receivers below, the first-seen date and the incoming/outgoing counts above cover the entire history.'
      : ' Top senders/receivers below cover the entire history.';

    if (this.deepScan?.reused === true) {
      const datePart = this.storedScanDateLabel
        ? ` of ${this.storedScanDateLabel}`
        : '';
      const staleTx = Number(this.deepScan?.staleTx ?? 0);
      const stalePart =
        staleTx > 0
          ? ` ${staleTx.toLocaleString('en-US')} newer transaction${
              staleTx === 1 ? '' : 's'
            } not merged in yet — this history was stored before incremental updates existed and will be refreshed on the next full scan.`
          : ' No new transactions since.';
      return `Full history reused from the stored scan${datePart}: all ${analyzed} transactions analyzed, no credits used.${stalePart}${coverSuffix}`;
    }

    if (this.deepScan?.mode === 'topup') {
      const newTx = Number(this.deepScan?.newTx ?? 0);
      return `Stored history updated: ${newTx.toLocaleString(
        'en-US'
      )} new transaction${
        newTx === 1 ? '' : 's'
      } merged — all ${analyzed} transactions analyzed${failedSuffix}.${coverSuffix}`;
    }

    return `Full history scanned: all ${analyzed} transactions analyzed${failedSuffix}.${coverSuffix}`;
  }

  get deepScanRunningMessage(): string {
    if (this.deepScan?.mode === 'topup') {
      const newTx = Number(this.deepScan?.newTx ?? 0);
      return `Updating the stored history: merging ${newTx.toLocaleString(
        'en-US'
      )} new transaction${
        newTx === 1 ? '' : 's'
      } added since the last scan — keep this page open.`;
    }
    const analyzed = (this.deepScan?.analyzed ?? 0).toLocaleString('en-US');
    const total = (
      this.deepScan?.total ||
      this.inv?.profile?.txCount ||
      0
    ).toLocaleString('en-US');
    return `Deep scan in progress: ${analyzed} of ${total} transactions analyzed. Top senders/receivers update automatically — keep this page open.`;
  }

  get txInCount(): number {
    return (this.inv?.transactions || []).filter(
      (t: any) => t?.direction === 'in'
    ).length;
  }

  get txOutCount(): number {
    return (this.inv?.transactions || []).filter(
      (t: any) => t?.direction === 'out'
    ).length;
  }

  get abuseSources(): Array<{
    source: string;
    status: string;
    count: number;
  }> {
    const sources = this.inv?.abuseSources;
    if (Array.isArray(sources) && sources.length > 0) return sources;
    return [
      {
        source: 'ChainAbuse',
        status: 'ok',
        count: this.inv?.abuseCount || 0,
      },
    ];
  }

  get detailDrawerTitle(): string {
    return this.drawerMeta[this.detailDrawer ?? '']?.title ?? 'Details';
  }

  get detailDrawerIcon(): string {
    return this.drawerMeta[this.detailDrawer ?? '']?.icon ?? 'fa-circle-info';
  }

  get detailDrawerIconClass(): string {
    return this.drawerMeta[this.detailDrawer ?? '']?.iconClass ?? '';
  }

  get navSections(): { id: string; label: string }[] {
    const sections: { id: string; label: string }[] = [];
    if (this.displayTopSenders.length || this.displayTopReceivers.length) {
      sections.push({ id: 'section-counterparties', label: 'Counterparties' });
      sections.push({ id: 'section-flow', label: 'Money Flow' });
    }
    if (this.inv?.transactions?.length) {
      sections.push({ id: 'section-timeline', label: 'Timeline' });
    }
    sections.push({ id: 'section-abuse', label: 'Abuse Check' });
    sections.push({ id: 'section-trace', label: 'Trace Funds' });
    if (this.inv?.lea) {
      sections.push({ id: 'section-lea', label: 'Legal Targets' });
    }
    sections.push({ id: 'section-risk', label: 'Risk Factors' });
    if (this.inv?.exposure) {
      sections.push({ id: 'section-exposure', label: 'Exposure' });
    }
    if (this.volumeChart) {
      sections.push({ id: 'section-chart', label: 'Volume' });
    }
    sections.push({ id: 'section-narrative', label: 'Narrative' });
    return sections;
  }

  get displayedTransactions(): any[] {
    const txs = this.filteredTransactions;
    return this.showAllTx ? txs : txs.slice(0, 10);
  }

  get filteredTransactions(): any[] {
    const items: any[] = this.inv?.transactions || [];
    const minV = this.txMinAmt.trim() === '' ? null : parseFloat(this.txMinAmt);
    const maxV = this.txMaxAmt.trim() === '' ? null : parseFloat(this.txMaxAmt);
    const fromSec = this.istLocalToEpochSec(this.txFromDate);
    const toSec = this.istLocalToEpochSec(this.txToDate);
    return items.filter((tx) => {
      if (this.txDirFilter !== 'all' && tx?.direction !== this.txDirFilter) {
        return false;
      }
      const v = Number(tx?.valueBtc ?? 0);
      if (minV != null && Number.isFinite(minV) && v < minV) return false;
      if (maxV != null && Number.isFinite(maxV) && v > maxV) return false;
      const ts = this.normSec(tx?.time);
      if (fromSec != null && ts < fromSec) return false;
      if (toSec != null && ts > toSec) return false;
      return true;
    });
  }

  get txActiveFilterCount(): number {
    return (
      (this.txDirFilter !== 'all' ? 1 : 0) +
      (this.txMinAmt.trim() !== '' ? 1 : 0) +
      (this.txMaxAmt.trim() !== '' ? 1 : 0) +
      (this.txFromDate ? 1 : 0) +
      (this.txToDate ? 1 : 0)
    );
  }

  get flowActiveFilterCount(): number {
    return this.flowMinAmt.trim() !== '' ? 1 : 0;
  }

  get hasFlowData(): boolean {
    return (
      this.displayTopSenders.length > 0 || this.displayTopReceivers.length > 0
    );
  }

  clearTxFilters(): void {
    this.txDirFilter = 'all';
    this.txMinAmt = '';
    this.txMaxAmt = '';
    this.txFromDate = '';
    this.txToDate = '';
    this.showAllTx = false;
  }

  clearFlowFilters(): void {
    this.flowMinAmt = '';
  }

  /** datetime-local value (YYYY-MM-DDTHH:mm) interpreted as IST → epoch seconds. */
  private istLocalToEpochSec(v: string): number | null {
    if (!v) return null;
    const ms = Date.parse(`${v}:00+05:30`);
    if (Number.isNaN(ms)) return null;
    return Math.floor(ms / 1000);
  }

  get volumeBars(): {
    label: string;
    received: number;
    sent: number;
    receivedPct: number;
    sentPct: number;
  }[] {
    return this.volumeChart?.bars ?? [];
  }

  /** Ryno TransactionChart params: month grouping, Y ticks, axis labels. */
  get volumeChart(): {
    symbol: string;
    yTicks: number[];
    max: number;
    bars: {
      label: string;
      received: number;
      sent: number;
      receivedPct: number;
      sentPct: number;
    }[];
  } | null {
    const txs: any[] = this.inv?.transactions || [];
    if (!txs.length) return null;

    const byMonth = new Map<string, { received: number; sent: number }>();
    for (const tx of txs) {
      const sec = this.normSec(tx.time);
      const d = new Date(sec * 1000);
      if (Number.isNaN(d.getTime())) continue;
      // Match Ryno: local calendar month key YYYY-MM
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const row = byMonth.get(key) || { received: 0, sent: 0 };
      if (tx.direction === 'in') row.received += Number(tx.valueBtc) || 0;
      else row.sent += Number(tx.valueBtc) || 0;
      byMonth.set(key, row);
    }

    const rows = Array.from(byMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, v]) => ({
        label,
        received: Number((v.received ?? 0).toFixed(8)),
        sent: Number((v.sent ?? 0).toFixed(8)),
      }));
    if (!rows.length) return null;

    const dataMax = Math.max(
      ...rows.map((r) => Math.max(r.received, r.sent)),
      0
    );
    const yTicks = this.niceVolumeTicks(dataMax);
    const max = yTicks[yTicks.length - 1] || 1;

    return {
      symbol: this.inv?.symbol || 'BTC',
      yTicks: [...yTicks].reverse(),
      max,
      bars: rows.map((r) => ({
        ...r,
        receivedPct: (r.received / max) * 100,
        sentPct: (r.sent / max) * 100,
      })),
    };
  }

  private niceVolumeTicks(max: number): number[] {
    if (!Number.isFinite(max) || max <= 0) return [0, 1];
    const rough = max / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(rough)));
    const residual = rough / mag;
    const step =
      residual <= 1 ? mag : residual <= 2 ? 2 * mag : residual <= 5 ? 5 * mag : 10 * mag;
    const top = Math.ceil(max / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= top + step * 1e-9; v += step) {
      ticks.push(Number(v.toPrecision(12)));
    }
    return ticks.length ? ticks : [0, top || 1];
  }

  fmtVolumeTip(n: number): string {
    return Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 8 });
  }

  showVolumeTooltip(
    bar: { label: string; received: number; sent: number },
    event: MouseEvent | PointerEvent
  ): void {
    const tipW = 168;
    const tipH = 84;
    const pad = 8;
    const x = event.clientX;
    const y = event.clientY;

    let left = x + 14;
    if (left + tipW > window.innerWidth - pad) {
      left = x - tipW - 14;
    }
    left = Math.max(pad, left);

    let top = y - tipH - 12;
    if (top < pad) {
      top = y + 16;
    }
    if (top + tipH > window.innerHeight - pad) {
      top = Math.max(pad, window.innerHeight - tipH - pad);
    }

    this.volumeTooltip = {
      label: bar.label,
      received: bar.received,
      sent: bar.sent,
      left: Math.round(left),
      top: Math.round(top),
    };
  }

  hideVolumeTooltip(): void {
    this.volumeTooltip = null;
  }

  /** Keep tip only while pointer is on a bar (scroll/leave often miss mouseleave). */
  @HostListener('document:pointermove', ['$event'])
  onDocumentPointerMoveVolumeTip(event: PointerEvent): void {
    if (!this.volumeTooltip) return;
    const t = event.target as Element | null;
    if (!t?.closest?.('.volume-chart .bar')) {
      this.hideVolumeTooltip();
    }
  }

  /** SVG Sankey geometry for Money Flow (Ryno-style ribbons). */
  get flowGraph(): {
    width: number;
    height: number;
    nodeW: number;
    nodeH: number;
    centerW: number;
    centerH: number;
    leftX: number;
    centerX: number;
    centerY: number;
    rightX: number;
    targetShort: string;
    entityName: string | null;
    inflows: {
      address: string;
      short: string;
      valueLabel: string;
      txCount: number;
      x: number;
      y: number;
      label?: string;
    }[];
    outflows: {
      address: string;
      short: string;
      valueLabel: string;
      txCount: number;
      x: number;
      y: number;
      label?: string;
    }[];
    inLinks: { d: string; stroke: number }[];
    outLinks: { d: string; stroke: number }[];
  } | null {
    const minV =
      this.flowMinAmt.trim() === '' ? null : parseFloat(this.flowMinAmt);
    const pass = (c: any) =>
      minV == null || !Number.isFinite(minV) || (c?.totalBtc ?? 0) >= minV;
    const senders = this.displayTopSenders.filter(pass).slice(0, 5);
    const receivers = this.displayTopReceivers.filter(pass).slice(0, 5);
    if (!senders.length && !receivers.length) {
      return null;
    }

    const W = 920;
    const NODE_W = 230;
    const NODE_H = 46;
    const ROW_GAP = 16;
    const CENTER_W = 190;
    const CENTER_H = 64;
    const PAD_Y = 20;
    const rows = Math.max(senders.length, receivers.length, 1);
    const H = Math.max(rows * (NODE_H + ROW_GAP) + PAD_Y * 2, 200);
    const centerY = H / 2;
    const leftX = 8;
    const centerX = (W - CENTER_W) / 2;
    const rightX = W - NODE_W - 8;

    const yFor = (i: number, count: number) => {
      const totalH = count * NODE_H + (count - 1) * ROW_GAP;
      const startY = (H - totalH) / 2;
      return startY + i * (NODE_H + ROW_GAP);
    };

    const linkPath = (x1: number, y1: number, x2: number, y2: number) => {
      const mx = (x1 + x2) / 2;
      return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
    };

    const maxVal = Math.max(
      ...senders.map((c: any) => c?.totalBtc ?? 0),
      ...receivers.map((c: any) => c?.totalBtc ?? 0),
      1e-12,
    );
    const strokeFor = (v: number) => 2 + ((v ?? 0) / maxVal) * 16;
    const fmtVal = (n: number) =>
      (n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 });
    const symbol = this.inv?.symbol || 'BTC';

    const inflows = senders.map((cp: any, i: number) => ({
      address: String(cp.address || ''),
      short: this.shortAddr(cp.address),
      valueLabel: `${fmtVal(cp.totalBtc)} ${symbol}`,
      txCount: cp.txCount ?? 0,
      x: leftX,
      y: yFor(i, senders.length),
      label: cp.label?.name || undefined,
    }));

    const outflows = receivers.map((cp: any, i: number) => ({
      address: String(cp.address || ''),
      short: this.shortAddr(cp.address),
      valueLabel: `${fmtVal(cp.totalBtc)} ${symbol}`,
      txCount: cp.txCount ?? 0,
      x: rightX,
      y: yFor(i, receivers.length),
      label: cp.label?.name || undefined,
    }));

    const inLinks = senders.map((cp: any, i: number) => {
      const y = yFor(i, senders.length) + NODE_H / 2;
      return {
        d: linkPath(leftX + NODE_W, y, centerX, centerY),
        stroke: strokeFor(cp.totalBtc ?? 0),
      };
    });

    const outLinks = receivers.map((cp: any, i: number) => {
      const y = yFor(i, receivers.length) + NODE_H / 2;
      return {
        d: linkPath(centerX + CENTER_W, centerY, rightX, y),
        stroke: strokeFor(cp.totalBtc ?? 0),
      };
    });

    return {
      width: W,
      height: H,
      nodeW: NODE_W,
      nodeH: NODE_H,
      centerW: CENTER_W,
      centerH: CENTER_H,
      leftX,
      centerX,
      centerY,
      rightX,
      targetShort: this.shortAddr(this.displayAddress),
      entityName: this.inv?.entityLabel?.name || null,
      inflows,
      outflows,
      inLinks,
      outLinks,
    };
  }

  constructor(
    private router: Router,
    private snackBar: MatSnackBar,
    private searchPollingService: SearchPollingService,
    private osintSearchService: OsintSearchService,
    private walletExportService: OsintWalletExportService,
    private searchServiceStatusService: SearchServiceStatusService,
  ) {}

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.searchId =
      this.selectedSearchResult?.osint_search_id ||
      this.selectedSearchResult?.job_id;

    if (!this.selectedSearchResult) {
      this.snackBar.open('No wallet search selected.', 'Close', {
        duration: 3000,
        panelClass: ['snackbar-error'],
      });
      this.router.navigate(['/osint-wallet-search']);
      return;
    }

    if (this.selectedSearchResult.done) {
      this.loadingCompletedSearchResult = true;
      this.getCompletedSearchResult();
    } else {
      this.pollForSearchResult();
    }

    setTimeout(() => this.setupSectionNav(), 80);
  }

  ngOnDestroy() {
    this.stopDeepScanPolling();
    this.teardownSectionNav();
    this.jobResultSubscription?.unsubscribe();
    this.vendorSyncSubscription?.unsubscribe();
    document.body.style.overflow = '';
  }

  onBackButtonClick() {
    this.router.navigate(['/osint-wallet-search']);
  }

  setViewTab(tab: 'results' | 'ai-analysis'): void {
    this.activeViewTab = tab;
    if (tab === 'results') {
      setTimeout(() => this.setupSectionNav(), 50);
    } else {
      this.teardownSectionNav();
    }
  }

  fmtBtc(n: number | null | undefined): string {
    if (typeof n !== 'number' || Number.isNaN(n)) return '0';
    return n.toLocaleString('en-US', { maximumFractionDigits: 8 });
  }

  /** Ryno fund-trace amount formatting (max 6 fraction digits). */
  fmtTrace(n: number | null | undefined): string {
    if (typeof n !== 'number' || Number.isNaN(n)) return '—';
    return n.toLocaleString('en-US', { maximumFractionDigits: 6 });
  }

  shortAddrTrace(addr: string): string {
    const value = String(addr || '');
    if (value.length <= 18) return value;
    return `${value.slice(0, 9)}…${value.slice(-5)}`;
  }

  traceHopIcon(node: any): string {
    if (node?.sanctioned) return 'fa-ban';
    if (node?.hop === 0) return 'fa-location-dot';
    if (node?.label?.name) return 'fa-building';
    if (node?.terminal) return 'fa-flag';
    return 'fa-circle-dot';
  }

  fiatPair(u: number | null | undefined, i: number | null | undefined): string {
    const us =
      typeof u === 'number' && Number.isFinite(u)
        ? `$${u.toLocaleString('en-US', { maximumFractionDigits: 2 })}`
        : null;
    const is =
      typeof i === 'number' && Number.isFinite(i)
        ? `₹${i.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
        : null;
    if (us && is) return `${us} · ${is}`;
    return us ?? is ?? '—';
  }

  usdStr(n: number | null | undefined): string {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
    return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  }

  inrStr(n: number | null | undefined): string {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
    return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  }

  openDetailDrawer(
    key: 'received' | 'sent' | 'balance' | 'transactions' | 'activity' | 'abuse'
  ): void {
    this.activeInfoHint = null;
    this.infoHintStyle = null;
    this.detailDrawer = key;
    document.body.style.overflow = 'hidden';
  }

  closeDetailDrawer(): void {
    this.detailDrawer = null;
    document.body.style.overflow = '';
  }

  /** Bilingual KPI / section explanations (Ryno InfoHint copy). */
  get kpiHints(): {
    received: { en: string; hi: string };
    sent: { en: string; hi: string };
    balance: { en: string; hi: string };
    transactions: { en: string; hi: string };
    firstSeen: { en: string; hi: string };
    lastSeen: { en: string; hi: string };
    addressAge: { en: string; hi: string };
    abuse: { en: string; hi: string };
    topSenders: { en: string; hi: string };
    topReceivers: { en: string; hi: string };
    risk: { en: string; hi: string };
    moneyFlow: { en: string; hi: string };
    timeline: { en: string; hi: string };
    volume: { en: string; hi: string };
    narrative: { en: string; hi: string };
    trace: { en: string; hi: string };
  } {
    const symbol = this.inv?.symbol || 'BTC';
    return {
      received: {
        en: `All the ${symbol} this address has ever got, added up. The small line below is what it is worth in money (dollars and rupees).`,
        hi: `इस पते ने अब तक जितना भी ${symbol} पाया है, उसका कुल जोड़। नीचे छोटी लाइन उसकी पैसे (डॉलर और रुपये) में कीमत है।`,
      },
      sent: {
        en: `All the ${symbol} this address has ever sent out, added up. The small line below is its value in money.`,
        hi: `इस पते ने अब तक जितना भी ${symbol} भेजा है, उसका कुल जोड़। नीचे छोटी लाइन उसकी पैसे में कीमत है।`,
      },
      balance: {
        en: `How much ${symbol} is left in this address right now. The small line is today's value in money.`,
        hi: `इस पते में अभी कितना ${symbol} बचा है। नीचे छोटी लाइन आज की पैसे में कीमत है।`,
      },
      transactions: {
        en: 'How many times this address has sent or got money. A higher number means the address is used a lot.',
        hi: 'इस पते ने कितनी बार पैसे भेजे या लिए। ज़्यादा संख्या मतलब पते का इस्तेमाल ज़्यादा हुआ है।',
      },
      firstSeen: {
        en: 'The date this address was used for the very first time.',
        hi: 'वह तारीख जब इस पते का पहली बार इस्तेमाल हुआ था।',
      },
      lastSeen: {
        en: 'The date this address was used most recently (the last time it moved money).',
        hi: 'वह तारीख जब इस पते का सबसे आखिरी बार इस्तेमाल हुआ (आखिरी बार पैसे चले)।',
      },
      addressAge: {
        en: 'How old this address is — the time from its first use until now.',
        hi: 'यह पता कितना पुराना है — पहले इस्तेमाल से अब तक का समय।',
      },
      abuse: {
        en: 'How many times people have complained that this address did something bad (like a scam or fraud). 0 is good.',
        hi: 'कितनी बार लोगों ने शिकायत की कि इस पते ने कुछ गलत किया (जैसे ठगी या धोखाधड़ी)। 0 होना अच्छा है।',
      },
      topSenders: {
        en: `These are the addresses that sent the most ${symbol} to this address. The number is the total amount they sent. If a name is shown (like an exchange), we know who they are.`,
        hi: `ये वे पते हैं जिन्होंने इस पते को सबसे ज़्यादा ${symbol} भेजा। संख्या मतलब उन्होंने कुल कितना भेजा। अगर नाम दिखे (जैसे एक्सचेंज), तो इसका मतलब हम जानते हैं कि वह कौन है।`,
      },
      topReceivers: {
        en: `These are the addresses that got the most ${symbol} from this address. The number is the total amount they received. If a name is shown (like an exchange), we know who they are.`,
        hi: `ये वे पते हैं जिन्हें इस पते से सबसे ज़्यादा ${symbol} मिला। संख्या मतलब उन्हें कुल कितना मिला। अगर नाम दिखे (जैसे एक्सचेंज), तो इसका मतलब हम जानते हैं कि वह कौन है।`,
      },
      risk: {
        en: `This score from 0 to 100 tells you how risky this address looks. 0-29 is Low (looks safe), 30-60 is Medium (be careful), and 61-100 is High (be very careful). This address scored ${this.inv?.riskScore ?? 0}, which we rate as ${this.inv?.riskLevel || 'Unknown'}.`,
        hi: `यह 0 से 100 का स्कोर बताता है कि पता कितना जोखिम भरा लगता है। 0-29 मतलब Low (सुरक्षित लगता है), 30-60 मतलब Medium (सावधान रहें), और 61-100 मतलब High (बहुत सावधान रहें)। इस पते का स्कोर ${this.inv?.riskScore ?? 0} है, जिसे हम ${this.inv?.riskLevel || 'Unknown'} मानते हैं।`,
      },
      moneyFlow: {
        en: 'A simple picture of money going in and out of this address. Names on the left sent money to it; names on the right got money from it. A thicker line means a bigger amount.',
        hi: 'इस पते में पैसा आने और जाने की आसान तस्वीर। बाईं तरफ वाले जिन्होंने इसे पैसा भेजा; दाईं तरफ वाले जिन्हें इससे पैसा मिला। मोटी लाइन मतलब ज़्यादा पैसा।',
      },
      timeline: {
        en: "A list of every transaction, newest first. A green '+' means money came in; a red '-' means money went out. 'Value on <date>' is what it was worth on the date of that transaction. 'Value as of <date>' is what the same coins were worth at the time of this search.",
        hi: "हर लेन-देन की सूची, सबसे नई सबसे ऊपर। हरा '+' मतलब पैसा आया; लाल '-' मतलब पैसा गया। 'Value on <तारीख>' मतलब उस लेन-देन वाले दिन उसकी कीमत। 'Value as of <तारीख>' मतलब इस खोज के समय उतने ही कॉइन की कीमत।",
      },
      volume: {
        en: 'How much this address moved over time. Taller bars mean more money moved in that period.',
        hi: 'समय के साथ इस पते ने कितना पैसा मूव किया। लंबी बार मतलब उस समय ज़्यादा पैसा मूव हुआ।',
      },
      narrative: {
        en: 'A short, plain-language summary of everything found in this report, in simple English and Hindi.',
        hi: 'इस रिपोर्ट में जो कुछ मिला, उसका छोटा और आसान सारांश — सरल अंग्रेज़ी और हिंदी दोनों में।',
      },
      trace: {
        en: 'Follow the biggest single flow of money step by step to see where it finally went, or where it first came from. It stops when it reaches a known exchange, mixer or blocked address.',
        hi: 'सबसे बड़े पैसे के प्रवाह को एक-एक कदम फॉलो करके देखें कि वह आखिर में कहाँ गया, या सबसे पहले कहाँ से आया। यह तब रुकता है जब किसी जाने-माने एक्सचेंज, मिक्सर या ब्लॉक किए गए पते तक पहुँच जाए।',
      },
    };
  }

  toggleInfoHint(id: string, event: Event): void {
    event.stopPropagation();
    this.infoHintToggleAt = Date.now();
    if (this.activeInfoHint === id) {
      this.activeInfoHint = null;
      this.infoHintStyle = null;
      return;
    }

    const trigger = event.currentTarget as HTMLElement | null;
    this.placeInfoHint(trigger);
    this.activeInfoHint = id;
  }

  private placeInfoHint(trigger: HTMLElement | null): void {
    if (!trigger || typeof window === 'undefined') {
      this.infoHintPlacement = 'bottom';
      this.infoHintStyle = null;
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const isNarrow = window.innerWidth <= 991;
    const viewportPad = 8;
    const headerOffset = 64; // keep clear of app-header
    const gap = 8;
    const popoverWidth = Math.min(isNarrow ? 320 : 288, window.innerWidth - viewportPad * 2);
    // Bilingual risk/KPI copy is taller than a single paragraph.
    const estimatedHeight = isNarrow ? 340 : 280;
    const spaceAbove = rect.top - headerOffset - viewportPad;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPad;

    let placement: 'top' | 'bottom';
    if (isNarrow) {
      // Phones/tablets: prefer below the trigger so the header never clips the panel.
      placement =
        spaceBelow >= Math.min(180, estimatedHeight) || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';
    } else if (spaceAbove >= estimatedHeight + gap) {
      placement = 'top';
    } else if (spaceBelow >= estimatedHeight + gap) {
      placement = 'bottom';
    } else {
      placement = spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }

    // place-top uses translateY(-100%); `top` is the popover's bottom edge.
    if (
      placement === 'top' &&
      rect.top - gap - estimatedHeight < headerOffset + viewportPad
    ) {
      placement = 'bottom';
    }

    this.infoHintPlacement = placement;

    let left = isNarrow
      ? (window.innerWidth - popoverWidth) / 2
      : rect.right - popoverWidth;
    left = Math.max(
      viewportPad,
      Math.min(left, window.innerWidth - popoverWidth - viewportPad)
    );

    let top: number;
    if (placement === 'bottom') {
      top = rect.bottom + gap;
      const maxTop = window.innerHeight - viewportPad - 96;
      top = Math.min(top, maxTop);
      top = Math.max(headerOffset + viewportPad, top);
    } else {
      top = rect.top - gap;
      const minBottomEdge = headerOffset + viewportPad + Math.min(estimatedHeight, Math.max(spaceAbove, 120));
      top = Math.max(top, minBottomEdge);
    }

    const anchorX = rect.left + rect.width / 2 - left;
    const arrowInset = Math.max(12, Math.min(popoverWidth - 12, anchorX));

    this.infoHintStyle = {
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.round(popoverWidth)}px`,
      maxHeight: `${Math.round(
        Math.min(
          window.innerHeight - headerOffset - viewportPad * 2,
          placement === 'bottom'
            ? window.innerHeight - top - viewportPad
            : top - headerOffset - viewportPad
        )
      )}px`,
      '--info-arrow-left': `${Math.round(arrowInset)}px`,
    };
  }

  private closeInfoHint(): void {
    this.activeInfoHint = null;
    this.infoHintStyle = null;
  }

  @HostListener('document:click')
  onDocumentClickCloseInfoHint(): void {
    if (Date.now() - this.infoHintToggleAt < 80) return;
    this.closeInfoHint();
  }

  @HostListener('window:resize')
  @HostListener('window:scroll')
  onViewportChangeCloseInfoHint(): void {
    if (this.activeInfoHint) this.closeInfoHint();
    if (this.volumeTooltip) this.hideVolumeTooltip();
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseDrawer(): void {
    if (this.activeInfoHint) {
      this.closeInfoHint();
      return;
    }
    if (this.volumeTooltip) {
      this.hideVolumeTooltip();
      return;
    }
    if (this.detailDrawer) this.closeDetailDrawer();
    else if (this.reportModalOpen) this.closeReportModal();
  }

  formatDateTimeClient(dateStr: string | null | undefined): string {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return 'N/A';
      return (
        d.toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }) + ' IST'
      );
    } catch {
      return 'N/A';
    }
  }

  formatDateClient(dateStr: string | null | undefined): string {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  }

  formatTimeClient(dateStr: string | null | undefined): string {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return '';
      return (
        d.toLocaleTimeString('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          minute: '2-digit',
        }) + ' IST'
      );
    } catch {
      return '';
    }
  }

  formatTxTime(time: number): string {
    const sec = this.normSec(time);
    if (!sec) return 'N/A';
    try {
      return (
        new Date(sec * 1000).toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }) + ' IST'
      );
    } catch {
      return 'N/A';
    }
  }

  /** Shorter date label for fiat lines (matches Ryno timeline). */
  formatTxDateShort(time: number | string | null | undefined): string {
    if (time == null || time === '') return 'N/A';
    try {
      const sec =
        typeof time === 'number'
          ? this.normSec(time)
          : Math.floor(new Date(time).getTime() / 1000);
      if (!sec || Number.isNaN(sec)) return 'N/A';
      return new Date(sec * 1000).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'N/A';
    }
  }

  get searchAsOfLabel(): string {
    return this.formatTxDateShort(this.inv?.searchedAt || null);
  }

  /** "as of <date>" or "now" — matches Ryno timeline copy. */
  get searchAsOfPhrase(): string {
    const label = this.searchAsOfLabel;
    return label && label !== 'N/A' ? `as of ${label}` : 'now';
  }

  /** Current-price fiat for a tx amount (Value as of search time). */
  txCurrentFiat(tx: any): string | null {
    const value = Number(tx?.valueBtc);
    const usdPrice = Number(this.inv?.usd?.price);
    const inrPrice = Number(this.inv?.inr?.price);
    if (!Number.isFinite(value) || !Number.isFinite(usdPrice)) return null;
    const usd = value * usdPrice;
    const inr = Number.isFinite(inrPrice) ? value * inrPrice : null;
    const pair = this.fiatPair(usd, inr);
    return pair === '—' ? null : pair;
  }

  shortAddr(addr: string): string {
    const value = String(addr || '');
    if (value.length <= 20) return value;
    return `${value.slice(0, 10)}…${value.slice(-8)}`;
  }

  /** Truncation for counterparties table; short enough to fit phone columns. */
  shortAddrCp(addr: string): string {
    const value = String(addr || '');
    if (value.length <= 22) return value;
    return `${value.slice(0, 10)}…${value.slice(-8)}`;
  }

  shortHash(hash: string): string {
    const value = String(hash || '');
    if (value.length <= 24) return value;
    return `${value.slice(0, 16)}…${value.slice(-6)}`;
  }

  private normSec(t: number): number {
    return t < 1e12 ? t : Math.floor(t / 1000);
  }

  copyText(text: string): void {
    const value = String(text || '').trim();
    if (!value) return;

    const done = () =>
      this.snackBar.open('Copied', 'Close', {
        duration: 1800,
        panelClass: ['snackbar-success'],
      });

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).then(done).catch(() => {
        this.fallbackCopy(value);
        done();
      });
      return;
    }
    this.fallbackCopy(value);
    done();
  }

  private fallbackCopy(text: string): void {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
    } catch {
      /* ignore */
    }
    document.body.removeChild(el);
  }

  runTrace(): void {
    const address = this.displayAddress;
    if (!address) {
      this.snackBar.open('No wallet address available for tracing.', 'Close', {
        duration: 3000,
        panelClass: ['snackbar-error'],
      });
      return;
    }

    this.traceLoading = true;
    this.traceError = null;
    this.traceResult = null;

    const payload: {
      address: string;
      direction: 'downstream' | 'upstream';
      maxHops: number;
      osint_search_id?: string;
    } = {
      address,
      direction: this.traceDirection,
      maxHops: this.traceDepth,
    };
    if (this.searchId) {
      payload.osint_search_id = this.searchId;
    }

    this.osintSearchService.runWalletTrace(payload).subscribe({
      next: (response) => {
        const data = response?.data ?? response;
        // Strip portal-only fields before rendering hop path.
        const {
          portal_trace_id: _portalTraceId,
          portal_osint_search_id: _portalSearchId,
          ...tracePayload
        } = data || {};
        this.traceResult = tracePayload;
        this.traceLoading = false;
        this.loadTraceHistory();
      },
      error: (err) => {
        this.traceLoading = false;
        this.traceError =
          err?.error?.message_key ||
          err?.error?.message ||
          err?.message ||
          'Trace failed. Please try again.';
      },
    });
  }

  openSavedTrace(entry: any): void {
    if (!entry?.result) return;
    this.traceResult = entry.result;
    this.traceDirection =
      entry.direction === 'upstream' ? 'upstream' : 'downstream';
    this.traceDepth = Number(entry.max_hops) || this.traceDepth;
    this.traceError = null;
    this.scrollToSection('section-trace');
  }

  formatTraceHistoryTime(value: string | Date | null | undefined): string {
    if (!value) return 'N/A';
    try {
      return (
        new Date(value).toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }) + ' IST'
      );
    } catch {
      return 'N/A';
    }
  }

  private loadTraceHistory(): void {
    if (!this.searchId && !this.displayAddress) {
      this.traceHistory = [];
      return;
    }

    this.traceHistoryLoading = true;
    this.osintSearchService
      .getWalletTraceHistory({
        osint_search_id: this.searchId || undefined,
        address: this.displayAddress || undefined,
        limit: 20,
      })
      .subscribe({
        next: (response) => {
          const data = response?.data ?? response;
          this.traceHistory = Array.isArray(data?.traces) ? data.traces : [];
          this.traceHistoryLoading = false;
        },
        error: () => {
          this.traceHistoryLoading = false;
        },
      });
  }

  investigateAddress(address: string): void {
    this.startWalletSearch(address);
  }

  /** Start a new wallet/tx search and open results automatically. */
  prefillWalletSearch(value: string): void {
    this.startWalletSearch(value);
  }

  private startWalletSearch(value: string): void {
    const query = String(value || '').trim();
    if (!query) return;
    this.closeDetailDrawer();
    this.router.navigate(['/osint-wallet-search'], {
      state: { autoSearch: true, searchQuery: query, openResult: true },
    });
  }

  lookupTransaction(hash: string): void {
    const value = String(hash || '').trim();
    if (!value) return;

    this.txLookupLoading = true;
    this.txLookupError = null;
    this.txLookupResult = null;

    this.osintSearchService.lookupWalletTransaction(value).subscribe({
      next: (response) => {
        this.txLookupResult = response?.data ?? response;
        this.txLookupLoading = false;
      },
      error: (err) => {
        this.txLookupLoading = false;
        this.txLookupError =
          err?.error?.message_key ||
          err?.error?.message ||
          err?.message ||
          'Transaction lookup failed.';
      },
    });
  }

  closeTxLookup(): void {
    this.txLookupResult = null;
    this.txLookupError = null;
  }

  get canConfirmDeepScan(): boolean {
    return (
      !this.deepScanConfirming &&
      this.deepScan?.has_sufficient_credits !== false
    );
  }

  get deepScanInsufficientMessage(): string {
    if (this.deepScanCreditError) return this.deepScanCreditError;
    const needed = this.deepScan?.credits || 0;
    const remaining = this.deepScan?.remaining_credits ?? 0;
    return `Insufficient credits. This scan needs ${needed} credit(s); you have ${remaining}.`;
  }

  approveDeepScan(): void {
    if (!this.canConfirmDeepScan) return;
    this.deepScanCreditError = null;
    this.deepScanConsent = 'approved';
    this.deepScanConfirming = true;
    this.pollDeepScan(true);
  }

  startDeepScanPreview(): void {
    if (!this.needsDeepScan || !this.displayAddress) return;
    if (this.deepScanConsent === 'declined' || this.deepScanPreviewLoading) {
      return;
    }
    this.deepScanCreditError = null;
    this.deepScanPreviewLoading = true;
    this.pollDeepScan(false);
  }

  declineDeepScan(): void {
    this.deepScanConsent = 'declined';
    this.deepScanConfirming = false;
    this.stopDeepScanPolling();
  }

  openReportModal(): void {
    this.reportModalOpen = true;
    this.reportCategory = '';
    this.reportNote = '';
  }

  closeReportModal(): void {
    if (this.reportSubmitting) return;
    this.reportModalOpen = false;
  }

  submitCommunityReport(): void {
    const address = this.displayAddress;
    if (!address || !this.reportCategory) {
      this.snackBar.open('Please choose a report category.', 'Close', {
        duration: 3000,
        panelClass: ['snackbar-error'],
      });
      return;
    }

    this.reportSubmitting = true;
    this.osintSearchService
      .submitWalletCommunityReport({
        address,
        category: this.reportCategory,
        note: this.reportNote?.trim() || undefined,
      })
      .subscribe({
        next: (response) => {
          this.reportSubmitting = false;
          this.reportModalOpen = false;
          const count = response?.data?.count;
          if (this.inv && (typeof count === 'number' || response?.data?.categories)) {
            this.inv = {
              ...this.inv,
              ...(typeof count === 'number' ? { communityReportCount: count } : {}),
              ...(response?.data?.categories
                ? { communityCategories: response.data.categories }
                : {}),
            };
          }
          this.snackBar.open(
            'Report submitted. Thank you for helping keep the community safe.',
            'Close',
            { duration: 3500, panelClass: ['snackbar-success'] },
          );
        },
        error: (err) => {
          this.reportSubmitting = false;
          this.snackBar.open(
            err?.error?.message_key ||
              err?.error?.message ||
              'Failed to submit report.',
            'Close',
            { duration: 3500, panelClass: ['snackbar-error'] },
          );
        },
      });
  }

  private resetDeepScanState(): void {
    this.stopDeepScanPolling();
    this.deepScan = null;
    this.deepScanConsent = 'pending';
    this.deepScanConfirming = false;
    this.deepScanPreviewLoading = false;
    this.deepScanCreditError = null;
  }

  private pollDeepScan(forceConfirm: boolean): void {
    if (this.deepScanConsent === 'declined') return;
    const address = this.displayAddress;
    if (!address) return;

    const generation = this.deepScanPollGeneration;
    const confirm = forceConfirm || this.deepScanConsent === 'approved';

    this.osintSearchService.getWalletDeepScan(address, confirm).subscribe({
      next: (response) => {
        if (generation !== this.deepScanPollGeneration) return;
        const data = response?.data ?? response;
        this.deepScan = data;
        this.deepScanConfirming = false;
        this.deepScanPreviewLoading = false;

        if (data?.credits_refunded > 0) {
          this.deepScanConsent = 'pending';
          this.snackBar.open(
            `${data.credits_refunded} credit(s) restored because the deep scan could not start.`,
            'Close',
            { duration: 4500, panelClass: ['snackbar-error'] },
          );
        }

        if (data?.status === 'awaiting_confirmation') {
          this.deepScanConsent = 'pending';
          if (data?.has_sufficient_credits === false) {
            this.deepScanCreditError =
              `Insufficient credits. This scan needs ${data?.credits || 0} credit(s); you have ${data?.remaining_credits ?? 0}.`;
          }
          return;
        }

        if (
          data?.status === 'done' ||
          data?.status === 'error' ||
          data?.status === 'unavailable'
        ) {
          return;
        }

        if (data?.status === 'running') {
          this.deepScanTimer = setTimeout(
            () => this.pollDeepScan(false),
            6000,
          );
        }
      },
      error: (err) => {
        if (generation !== this.deepScanPollGeneration) return;
        const wasConfirming = this.deepScanConfirming || forceConfirm;
        const status = err?.status || err?.error?.error_code;
        const message =
          err?.error?.message_key ||
          err?.error?.message ||
          err?.message ||
          '';
        const isCreditError =
          status === 422 || /insufficient|not enough credits/i.test(message);

        this.deepScanConfirming = false;
        this.deepScanPreviewLoading = false;

        if (isCreditError) {
          this.deepScanConsent = 'pending';
          const creditError = message || 'Insufficient Credits';
          this.deepScanCreditError = creditError;
          this.snackBar.open(creditError, 'Close', {
            duration: 4000,
            panelClass: ['snackbar-error'],
          });
          return;
        }

        if (wasConfirming) {
          this.deepScanConsent = 'pending';
          this.snackBar.open(
            'Deep scan failed. Any deducted credits have been restored.',
            'Close',
            { duration: 4500, panelClass: ['snackbar-error'] },
          );
          return;
        }

        this.deepScan = {
          ...(this.deepScan || {}),
          status: 'unavailable',
        };
      },
    });
  }

  private stopDeepScanPolling(): void {
    this.deepScanPollGeneration += 1;
    if (this.deepScanTimer) {
      clearTimeout(this.deepScanTimer);
      this.deepScanTimer = null;
    }
  }

  scrollToSection(sectionId: string, event?: Event): void {
    event?.preventDefault();
    const target = document.getElementById(sectionId);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.activeNavSection = sectionId;
  }

  jumpToTop(): void {
    const host = document.querySelector(
      '.ryno-investigation',
    )?.closest('.h-100p.overflow-y-scroll') as HTMLElement | null;
    if (host) host.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
    this.activeNavSection = null;
    this.isAtTop = true;
  }

  private setupSectionNav(): void {
    this.teardownSectionNav();
    if (this.activeViewTab !== 'results') return;

    const ids = this.navSections.map((s) => s.id);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);

    if (els.length) {
      this.sectionObserver = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((e) => e.isIntersecting);
          if (!visible.length) return;
          const topMost = visible.reduce((a, b) =>
            a.boundingClientRect.top <= b.boundingClientRect.top ? a : b,
          );
          this.activeNavSection = topMost.target.id;
        },
        { rootMargin: '-120px 0px -55% 0px', threshold: 0 },
      );
      els.forEach((el) => this.sectionObserver?.observe(el));
    }

    const host = document.querySelector(
      '.ryno-investigation',
    )?.closest('.h-100p.overflow-y-scroll') as HTMLElement | null;
    const onScroll = () => {
      const top = host ? host.scrollTop : window.scrollY;
      this.isAtTop = top < 240;
      if (this.activeInfoHint) this.closeInfoHint();
      if (this.volumeTooltip) this.hideVolumeTooltip();
    };
    onScroll();
    if (host) {
      host.addEventListener('scroll', onScroll, { passive: true });
      this.scrollHostListener = () => host.removeEventListener('scroll', onScroll);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
      this.scrollHostListener = () =>
        window.removeEventListener('scroll', onScroll);
    }
  }

  private teardownSectionNav(): void {
    this.sectionObserver?.disconnect();
    this.sectionObserver = undefined;
    this.scrollHostListener?.();
    this.scrollHostListener = undefined;
  }

  getCompletedSearchResult() {
    const payload = {
      type: 'wallet',
      searchId: this.searchId,
    };
    this.loadingCompletedSearchResult = true;
    const originalSearchedWallet = this.selectedSearchResult?.searched_wallet;

    this.osintSearchService
      .getCompletedSearchResult(payload.type, payload.searchId)
      .subscribe({
        next: (response) => {
          const rawSearchResultData = response?.data;
          this.applyWalletResultsPayload(
            rawSearchResultData,
            originalSearchedWallet,
          );

          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
          setTimeout(() => this.setupSectionNav(), 80);

          if (rawSearchResultData?.background_refresh_pending) {
            this.watchVendorSyncViaJobPollThenReload();
          }
        },
        error: () => {
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
          setTimeout(() => this.setupSectionNav(), 80);
        },
      });
  }

  /**
   * Apply GET /osint/results?type=wallet payload.
   * Prefer vendor investigation shape; fall back to paginated data_list.extra_data.vendor.
   */
  private applyWalletResultsPayload(
    rawSearchResultData: any,
    originalSearchedWallet?: string,
  ): void {
    if (!rawSearchResultData || typeof rawSearchResultData !== 'object') {
      return;
    }

    const investigation = this.extractInvestigationPayload(rawSearchResultData);
    const listRow = Array.isArray(rawSearchResultData?.data_list)
      ? rawSearchResultData.data_list[0]
      : null;

    const searchedWallet =
      rawSearchResultData?.searched_wallet ||
      rawSearchResultData?.query ||
      investigation?.address ||
      listRow?.extra_data?.vendor?.address ||
      originalSearchedWallet ||
      this.selectedSearchResult?.searched_wallet ||
      '';

    this.selectedSearchResult = {
      ...(this.selectedSearchResult || {}),
      ...(listRow || {}),
      osint_search_id:
        rawSearchResultData?.osint_search_id ||
        listRow?.osint_search_id ||
        this.selectedSearchResult?.osint_search_id,
      result_id:
        rawSearchResultData?.result_id ||
        listRow?.result_id ||
        this.selectedSearchResult?.result_id,
      job_id:
        rawSearchResultData?.portal_job_id ||
        this.selectedSearchResult?.job_id,
      searched_wallet: searchedWallet,
      done:
        rawSearchResultData?.done ??
        this.selectedSearchResult?.done ??
        true,
      background_refresh_pending: Boolean(
        rawSearchResultData?.background_refresh_pending,
      ),
    };

    this.notes =
      rawSearchResultData?.notes ||
      this.selectedSearchResult?.notes ||
      this.notes ||
      '';

    if (investigation) {
      this.inv = investigation;
      this.resetDeepScanState();
      this.loadTraceHistory();
    }
  }

  private extractInvestigationPayload(raw: any): any | null {
    if (!raw || typeof raw !== 'object') return null;

    // Vendor-shaped envelope from our API (preferred).
    if (
      typeof raw.address === 'string' ||
      typeof raw.riskScore === 'number' ||
      raw.profile
    ) {
      const {
        portal_job_id: _portalJobId,
        osint_search_id: _osintSearchId,
        result_id: _resultId,
        notes: _notes,
        background_refresh_pending: _pending,
        done: _done,
        query_type: _queryType,
        query: _query,
        searched_wallet: _searchedWallet,
        ...investigation
      } = raw;
      return investigation;
    }

    const listRow = Array.isArray(raw.data_list) ? raw.data_list[0] : null;
    const vendor = listRow?.extra_data?.vendor;
    if (vendor && typeof vendor === 'object') {
      return vendor;
    }

    return null;
  }

  private watchVendorSyncViaJobPollThenReload() {
    const jobId = this.selectedSearchResult?.job_id;
    if (!jobId) return;
    this.vendorSyncSubscription?.unsubscribe();
    this.vendorSyncSubscription = this.searchPollingService
      .getJobResult(jobId)
      .pipe(
        filter((s) => !!s && isOsintJobVendorSyncComplete(s)),
        take(1),
      )
      .subscribe(() => this.getCompletedSearchResult());
  }

  pollForSearchResult() {
    const jobId = this.selectedSearchResult.job_id;
    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;
    const originalSearchedWallet = this.selectedSearchResult?.searched_wallet;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (!response) return;
          if (isOsintJobVendorSyncComplete(response)) {
            this.getCompletedSearchResult();
          } else {
            this.selectedSearchResult = {
              ...this.selectedSearchResult,
              done: response.done || false,
              status: response.status || 'pending',
              searched_wallet:
                response?.searched_wallet || originalSearchedWallet,
            };
          }
        },
        error: () => {
          this.loadingPolledSearchResult = false;
        },
      });
  }

  async onExportOptionClick(exportFormat: 'pdf' | 'zip') {
    if (this.exportBusy) return;
    const result = this.slimInvForExport();
    if (!result?.address) {
      this.snackBar.open('Export requires a completed investigation.', 'Close', {
        duration: 3500,
        panelClass: ['snackbar-error'],
      });
      return;
    }

    this.exportingFormat = exportFormat;
    try {
      await this.walletExportService.exportReport({
        result,
        format: exportFormat,
        generatedBy: this.getGeneratedBy(),
      });
      this.snackBar.open(
        exportFormat === 'zip' ? 'Report ZIP exported' : 'Report exported',
        'Close',
        { duration: 2500, panelClass: ['snackbar-success'] },
      );
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Failed to export report';
      this.snackBar.open(message, 'Close', {
        duration: 5000,
        panelClass: ['snackbar-error'],
      });
    } finally {
      this.exportingFormat = null;
    }
  }

  /** PDF/ZIP only include the first 25 txs, matching the printed report. */
  private slimInvForExport(): any | undefined {
    if (!this.inv?.address) return undefined;
    const txs = Array.isArray(this.inv.transactions)
      ? this.inv.transactions.slice(0, 25)
      : [];
    return {
      ...this.inv,
      transactions: txs,
    };
  }

  private getGeneratedBy(): string {
    try {
      const raw = localStorage.getItem('USER_PORTAL');
      const userPortal = raw ? JSON.parse(raw) : null;
      const details = userPortal?.loginDetails || {};
      return String(
        details.user_name || details.user_email || details.email || '',
      ).trim();
    } catch {
      return '';
    }
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;
    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        { duration: 3000, panelClass: ['snackbar-error'] },
      );
      return;
    }

    this.osintSearchService
      .updateWalletNotes(resultId, { notes: this.notes.trim() })
      .subscribe({
        next: () =>
          this.snackBar.open('Notes updated successfully!', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-success'],
          }),
        error: () =>
          this.snackBar.open('Failed to update notes.', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-error'],
          }),
      });
  }
}
